// ============================================================
// FILE: LeaveReview.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\LeaveReview.js
// ============================================================
// PURPOSE:
//   Shared review form used by BOTH buyers and gardeners after a
//   job is completed. The same component renders for either side —
//   it reads the logged-in user's type from AuthContext to decide
//   wording ("review the gardener" vs "review the buyer").
//
// ROUTES (both point to this same component):
//   /buyer/job/:id/review      — buyer reviewing the gardener
//   /gardener/job/:id/review   — gardener reviewing the buyer
//   (id = job_id in both cases)
//
// CALLS API:
//   GET  /api/jobs/:id   — load job title for display
//   POST /api/reviews    — submit the review
//       body: { job_id, star_rating, comment }
//
// USED BY:
//   JobDetail.js (buyer side) shows "Leave a Review" once status = completed
//   GardenerDashboard.js (gardener side) — same trigger condition
//
// NOTES FOR MAINTAINERS:
//   - The stored proc usp_review_submit figures out who is being
//     reviewed (it compares reviewer_user_id against the job's buyer
//     and accepted gardener) — the front end doesn't need to know
//     the other party's user_id, just the job_id.
//   - One review per job per reviewer is enforced server-side; if a
//     user tries twice the proc raises an error that we display here.
//   - When both sides have reviewed, the job status auto-advances to
//     'reviewed' inside the proc — no extra action needed from here.
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';

export default function LeaveReview() {
    const { id }    = useParams(); // job_id from the URL
    const navigate  = useNavigate();
    const { user }  = useAuth();   // tells us if this is a buyer or gardener reviewing

    const [job,     setJob]     = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');

    // Star rating state — rating is the committed value, hover is for the
    // preview effect as the mouse moves over the stars before clicking
    const [rating,  setRating]  = useState(0);
    const [hover,   setHover]   = useState(0);
    const [comment, setComment] = useState('');

    // Load job title to show "Job: <title>" above the form
    useEffect(() => {
        client.get(`/jobs/${id}`)
            .then(r => setJob(r.data.job))
            .catch(err => setError(err.response?.data?.error || 'Failed to load job.'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) {
            setError('Please select a star rating.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            await client.post('/reviews', {
                job_id:      parseInt(id),
                star_rating: rating,
                comment:     comment || null,
            });
            // Send the user back to their own dashboard based on their type
            navigate(user.user_type === 'buyer' ? '/buyer/dashboard' : '/gardener/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit review.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.loading}>Loading...</div>;
    if (!job)    return <div style={styles.loading}>{error}</div>;

    const isBuyer        = user.user_type === 'buyer';
    const revieweeLabel  = isBuyer ? 'the gardener' : 'the buyer';

    return (
        <div style={styles.page}>
            <div style={styles.backRow}>
                <Link to={isBuyer ? `/buyer/job/${id}` : '/gardener/dashboard'} style={styles.back}>← Back</Link>
            </div>

            <h1 style={styles.title}>Leave a Review</h1>
            <p style={styles.sub}>Job: <strong>{job.title}</strong></p>

            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit} style={styles.form}>
                {/* ── Star rating picker ── */}
                <div style={styles.starsSection}>
                    <label style={styles.label}>How was your experience with {revieweeLabel}?</label>
                    <div style={styles.stars}>
                        {[1,2,3,4,5].map(n => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setRating(n)}
                                onMouseEnter={() => setHover(n)}
                                onMouseLeave={() => setHover(0)}
                                style={styles.starBtn}
                            >
                                {/* Star fills gold if hovered OR already selected up to this position */}
                                <span style={{
                                    color: (hover || rating) >= n ? '#FFC107' : '#ddd',
                                    fontSize: '2.4rem',
                                }}>★</span>
                            </button>
                        ))}
                    </div>
                    <p style={styles.ratingLabel}>
                        {rating === 5 && '⭐ Excellent!'}
                        {rating === 4 && 'Good'}
                        {rating === 3 && 'Average'}
                        {rating === 2 && 'Below average'}
                        {rating === 1 && 'Poor'}
                        {rating === 0 && '\u00A0' /* non-breaking space keeps layout height stable */}
                    </p>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Comments (optional)</label>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        style={styles.textarea}
                        placeholder={isBuyer
                            ? "What was the gardener like? Quality of work, punctuality, communication..."
                            : "What was the buyer like? Clear instructions, easy access, paid on time..."}
                    />
                </div>

                <button type="submit" style={styles.btnSubmit} disabled={saving}>
                    {saving ? 'Submitting...' : 'Submit Review'}
                </button>
            </form>
        </div>
    );
}

// ── Inline styles (kept local to this component) ──────────
const styles = {
    page:         { maxWidth:'560px', margin:'0 auto', padding:'32px 24px' },
    loading:      { textAlign:'center', padding:'60px', color:'#666' },
    backRow:      { marginBottom:'16px' },
    back:         { color:'#2d6a4f', fontSize:'0.9rem' },
    title:        { color:'#2d6a4f', marginBottom:'6px' },
    sub:          { color:'#666', marginBottom:'24px' },
    error:        { backgroundColor:'#fee', border:'1px solid #fcc', borderRadius:'6px', padding:'10px', color:'#c00', marginBottom:'16px' },
    form:         { background:'white', borderRadius:'12px', padding:'32px', boxShadow:'0 2px 12px rgba(0,0,0,0.08)' },
    starsSection: { textAlign:'center', marginBottom:'24px' },
    label:        { display:'block', fontWeight:'600', marginBottom:'12px', color:'#333', fontSize:'0.95rem' },
    stars:        { display:'flex', justifyContent:'center', gap:'4px' },
    starBtn:      { background:'none', border:'none', cursor:'pointer', padding:'2px' },
    ratingLabel:  { marginTop:'8px', fontWeight:'bold', color:'#2d6a4f', minHeight:'1.2em' },
    field:        { marginBottom:'20px' },
    textarea:     { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'1rem', height:'110px', resize:'vertical', boxSizing:'border-box' },
    btnSubmit:    { width:'100%', padding:'14px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer' },
};
